import * as React from 'react';

/**
 * RightPaneMode - which utility panel is active in the RightUtilityPane.
 *
 * A string matching the `panelKey` of the active right-rail item (e.g. 'chat',
 * 'sync-log', 'context-docs', 'settings'), or null when no panel is open.
 *
 * Previously a closed union; widened to string so DB-driven panel keys can be
 * used without requiring a type update for each new panel.
 */
export type RightPaneMode = string | null;

/**
 * LayoutState - centralized shell layout state
 *
 * Manages visibility and sizing of all shell panes as defined in
 * context/frontend-architecture.md §7–8
 */
export interface LayoutState {
  /** LeftNavPane collapsed state */
  leftNavCollapsed: boolean;

  /** ContextPane visibility */
  contextPaneVisible: boolean;

  /** ContextPane width in pixels */
  contextPaneWidth: number;

  /** RightUtilityPane visibility */
  rightPaneVisible: boolean;

  /** Active mode in RightUtilityPane */
  rightPaneMode: RightPaneMode;

  /** RightUtilityPane width in pixels */
  rightPaneWidth: number;

  /** BottomPane visibility within MainPane */
  bottomPaneVisible: boolean;

  /** BottomPane height in pixels */
  bottomPaneHeight: number;

  /**
   * Whether the right navigation rail strip is enabled for this page.
   * The rail is always visible when enabled; the pane is toggled by clicking rail icons.
   */
  rightRailEnabled: boolean;
}

/**
 * LayoutConfig - page-driven layout configuration
 *
 * Pages use this interface to declare which panes they need.
 * Simplifies layout control by allowing pages to request a complete
 * configuration rather than imperatively calling multiple setters.
 */
export interface LayoutConfig {
  /** Show ContextPane (default: false) */
  showContextPane?: boolean;

  /** Show BottomPane within MainPane (default: false) */
  showBottomPane?: boolean;

  /** RightUtilityPane configuration */
  showRightUtilityPane?:
    | boolean
    | {
        /** Enable and set active mode */
        mode: RightPaneMode;
      };

  /** Collapse the left nav pane on mount (default: unchanged) */
  collapseLeftNav?: boolean;

  /**
   * Show the right navigation rail strip.
   * When false, also closes the right pane and clears the active mode.
   * Default: false
   */
  showRightRail?: boolean;
}

/**
 * LayoutActions - state update methods
 */
export interface LayoutActions {
  toggleLeftNav: () => void;
  setLeftNavCollapsed: (collapsed: boolean) => void;

  /** Restore the displayed nav-collapse state to the user's saved preference. */
  resetLeftNavToPreference: () => void;

  setContextPaneVisible: (visible: boolean) => void;
  setContextPaneWidth: (width: number) => void;

  setRightPaneVisible: (visible: boolean) => void;
  setRightPaneMode: (mode: RightPaneMode) => void;
  setRightPaneWidth: (width: number) => void;

  setBottomPaneVisible: (visible: boolean) => void;
  setBottomPaneHeight: (height: number) => void;

  /** Enable or disable the right navigation rail strip */
  setRightRailEnabled: (enabled: boolean) => void;

  /**
   * setLayoutConfig - apply page-driven layout configuration
   *
   * Pages should use this method to declare their layout needs.
   * The shell will react to the configuration automatically.
   *
   * @example
   * ```tsx
   * // Show context pane and right pane in chat mode
   * setLayoutConfig({
   *   showContextPane: true,
   *   showRightUtilityPane: { mode: 'chat' }
   * });
   * ```
   */
  setLayoutConfig: (config: LayoutConfig) => void;
}

export type LayoutContextValue = LayoutState & LayoutActions;

const LayoutContext = React.createContext<LayoutContextValue | null>(null);

/**
 * Default pane sizes from CSS tokens
 * These match the values in global.css
 */
const DEFAULT_CONTEXT_PANE_WIDTH = 280;
const DEFAULT_RIGHT_PANE_WIDTH = 360;
const DEFAULT_BOTTOM_PANE_HEIGHT = 240;

/**
 * localStorage keys for persisting pane sizes
 */
const STORAGE_KEYS = {
  leftNavCollapsed: 'protoss.pane.leftNav.collapsed',
  contextPaneWidth: 'protoss.pane.context.width',
  rightPaneWidth: 'protoss.pane.rightUtility.width',
  bottomPaneHeight: 'protoss.pane.bottom.height',
} as const;

/**
 * LayoutProvider - provides centralized layout state management
 *
 * Manages all shell pane visibility and sizing with localStorage persistence.
 * Layout state must not be scattered across components (architecture.md §7).
 */
export function LayoutProvider({ children }: { children: React.ReactNode }) {
  // Persisted user preference for the left nav - changed only by the manual
  // toggle (LeftNavPane button), never by a page's transient collapseLeftNav.
  const [leftNavPreference, setLeftNavPreference] = React.useState<boolean>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.leftNavCollapsed);
    return stored ? stored === 'true' : false;
  });
  // Effective/displayed collapse state. Initialised from the preference; focus
  // pages override it transiently without touching (or persisting) the preference.
  const [leftNavCollapsed, setLeftNavCollapsedState] = React.useState<boolean>(leftNavPreference);
  // Live mirror of the preference so the unmount restore reads the current value
  // rather than a stale per-page snapshot.
  const leftNavPreferenceRef = React.useRef(leftNavPreference);
  React.useEffect(() => {
    leftNavPreferenceRef.current = leftNavPreference;
  }, [leftNavPreference]);

  const [contextPaneVisible, setContextPaneVisible] = React.useState(false);

  const [contextPaneWidth, setContextPaneWidthState] = React.useState<number>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.contextPaneWidth);
    return stored ? parseInt(stored, 10) : DEFAULT_CONTEXT_PANE_WIDTH;
  });

  const [rightPaneVisible, setRightPaneVisible] = React.useState(false);
  const [rightPaneMode, setRightPaneMode] = React.useState<RightPaneMode>(null);

  const [rightPaneWidth, setRightPaneWidthState] = React.useState<number>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.rightPaneWidth);
    return stored ? parseInt(stored, 10) : DEFAULT_RIGHT_PANE_WIDTH;
  });

  const [bottomPaneVisible, setBottomPaneVisible] = React.useState(false);

  const [bottomPaneHeight, setBottomPaneHeightState] = React.useState<number>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.bottomPaneHeight);
    return stored ? parseInt(stored, 10) : DEFAULT_BOTTOM_PANE_HEIGHT;
  });

  const [rightRailEnabled, setRightRailEnabled] = React.useState(false);

  // Persist only the user preference to localStorage (not transient overrides).
  React.useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.leftNavCollapsed, String(leftNavPreference));
  }, [leftNavPreference]);

  // Persist contextPaneWidth to localStorage
  const setContextPaneWidth = React.useCallback((width: number) => {
    setContextPaneWidthState(width);
    localStorage.setItem(STORAGE_KEYS.contextPaneWidth, String(width));
  }, []);

  // Persist rightPaneWidth to localStorage
  const setRightPaneWidth = React.useCallback((width: number) => {
    setRightPaneWidthState(width);
    localStorage.setItem(STORAGE_KEYS.rightPaneWidth, String(width));
  }, []);

  // Persist bottomPaneHeight to localStorage
  const setBottomPaneHeight = React.useCallback((height: number) => {
    setBottomPaneHeightState(height);
    localStorage.setItem(STORAGE_KEYS.bottomPaneHeight, String(height));
  }, []);

  // Manual collapse change (LeftNavPane button): updates both the effective
  // state and the persisted preference.
  const setLeftNavCollapsed = React.useCallback((collapsed: boolean) => {
    setLeftNavCollapsedState(collapsed);
    setLeftNavPreference(collapsed);
  }, []);
  const toggleLeftNav = React.useCallback(() => {
    setLeftNavCollapsedState((prev) => {
      const next = !prev;
      setLeftNavPreference(next);
      return next;
    });
  }, []);
  // Page-driven transient override: changes the displayed state only, leaving the
  // saved preference (and localStorage) untouched.
  const overrideLeftNavCollapsed = React.useCallback((collapsed: boolean) => {
    setLeftNavCollapsedState(collapsed);
  }, []);
  // Restore the displayed state to the saved preference (used on page unmount).
  const resetLeftNavToPreference = React.useCallback(() => {
    setLeftNavCollapsedState(leftNavPreferenceRef.current);
  }, []);

  /**
   * setLayoutConfig - apply page-driven layout configuration
   *
   * Accepts a LayoutConfig object and updates pane visibility accordingly.
   * This is the recommended way for pages to control layout.
   */
  const setLayoutConfig = React.useCallback((config: LayoutConfig) => {
    // LeftNavPane - transient override; does not touch the saved preference
    if (config.collapseLeftNav !== undefined) {
      overrideLeftNavCollapsed(config.collapseLeftNav);
    }

    // ContextPane
    if (config.showContextPane !== undefined) {
      setContextPaneVisible(config.showContextPane);
    }

    // BottomPane
    if (config.showBottomPane !== undefined) {
      setBottomPaneVisible(config.showBottomPane);
    }

    // RightUtilityPane
    if (config.showRightUtilityPane !== undefined) {
      if (typeof config.showRightUtilityPane === 'boolean') {
        setRightPaneVisible(config.showRightUtilityPane);
        if (!config.showRightUtilityPane) {
          setRightPaneMode(null);
        }
      } else {
        // Object with mode
        setRightPaneVisible(true);
        setRightPaneMode(config.showRightUtilityPane.mode);
      }
    }

    // Right rail strip
    if (config.showRightRail !== undefined) {
      setRightRailEnabled(config.showRightRail);
      if (!config.showRightRail) {
        // Closing the rail also collapses the pane
        setRightPaneVisible(false);
        setRightPaneMode(null);
      }
    }
  }, []);

  const value: LayoutContextValue = React.useMemo(
    () => ({
      // State
      leftNavCollapsed,
      contextPaneVisible,
      contextPaneWidth,
      rightPaneVisible,
      rightPaneMode,
      rightPaneWidth,
      bottomPaneVisible,
      bottomPaneHeight,
      rightRailEnabled,

      // Actions
      toggleLeftNav,
      setLeftNavCollapsed,
      resetLeftNavToPreference,
      setContextPaneVisible,
      setContextPaneWidth,
      setRightPaneVisible,
      setRightPaneMode,
      setRightPaneWidth,
      setBottomPaneVisible,
      setBottomPaneHeight,
      setRightRailEnabled,
      setLayoutConfig,
    }),
    [
      leftNavCollapsed,
      contextPaneVisible,
      contextPaneWidth,
      rightPaneVisible,
      rightPaneMode,
      rightPaneWidth,
      bottomPaneVisible,
      bottomPaneHeight,
      rightRailEnabled,
      toggleLeftNav,
      setLeftNavCollapsed,
      resetLeftNavToPreference,
      setContextPaneWidth,
      setRightPaneWidth,
      setBottomPaneHeight,
      setLayoutConfig,
    ]
  );

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
}

/**
 * useLayout - hook to access layout state and actions
 *
 * Must be called within a LayoutProvider.
 */
export function useLayout(): LayoutContextValue {
  const context = React.useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
}

/**
 * useLayoutConfig - declarative hook for page-driven layout control
 *
 * Automatically applies layout configuration when the component mounts
 * and resets to default when it unmounts. This is the recommended way
 * for pages to control shell layout.
 *
 * @param config - Layout configuration for the page
 *
 * @example
 * ```tsx
 * function ProjectDetailPage() {
 *   // Show context pane and right pane in timeline mode
 *   useLayoutConfig({
 *     showContextPane: true,
 *     showRightUtilityPane: { mode: 'timeline' }
 *   });
 *
 *   return <div>Project details...</div>;
 * }
 * ```
 */
export function useLayoutConfig(config: LayoutConfig): void {
  const { setLayoutConfig, resetLeftNavToPreference } = useLayout();

  // useLayoutEffect fires synchronously before the browser paints, ensuring
  // the grid columns are correct on the very first frame - no flash or jump.
  React.useLayoutEffect(() => {
    setLayoutConfig(config);

    // Reset volatile layout state on unmount so the next page always starts clean.
    return () => {
      setLayoutConfig({
        showContextPane: false,
        showBottomPane: false,
        showRightUtilityPane: false,
        showRightRail: false,
      });
      // Drop any transient collapseLeftNav override this page applied, returning
      // the nav to the user's saved preference. Reads the live preference (not a
      // per-page snapshot), so chained focus pages and in-page toggles both
      // resolve correctly.
      if (config.collapseLeftNav !== undefined) {
        resetLeftNavToPreference();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally omit config from deps - static layout declared at mount
}
